import { FileExcelOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  Pagination,
  Row,
  Select,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { debounce } from 'lodash'
import { runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import AccessControlAction from '../../../components/AccessControlAction'
import { TableBottomPaginationBlock } from '../../../components/Common/Table'
import TableComponent from '../../../components/Common/TableComponent'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import EmptyContent from '../../../components/EmptyContent'
import PageTitle from '../../../components/PageTitle'
import AppPageHeader from '../../../components/AppPageHeader'
import { ACL_ACTION_TYPE, TYPE_STATUS } from '../../../constants'
import DashboardLayout from '../../../layouts/DashboardLayout'
import { filterStatus } from './constant'
import { ExportExcel } from './ExportExcel/ExportExcel'
import FeedbackBoardCreate from './FeedbackBoardCreate'
import {
  FEEDBACK_BOARD_TAB,
  getBoardTypeByTab,
  getFeedbackTabFromRecord,
  getPlykSuffixByTab,
  isDelegateFeedbackTab,
  toDisplayDocumentNumber,
} from './feedbackBoardType'
import { AppTablePagination } from '../../../components/AppTable/AppTablePagination'
import { AppTable } from '../../../components/AppTable/AppTable'
import { AddCircleOutlineIcon } from '../../../components/AppIcon'
const { Option } = Select

const { RangePicker } = DatePicker
const { Title } = Typography
const { TabPane } = Tabs

const FeedbackBoardPage = props => {
  const {
    selectPeopleStore,
    documentAccessibilityStore,
    authenticationStore,
    loadingAnimationStore,
    FeedbackProcedureStore,
    history,
    location,
  } = props
  const [form] = Form.useForm()
  const [openFeedbackHDQT, setOpenFeedbackHDQT] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [dateRange, setDateRange] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const locationFeedbackTab = location?.state?.feedbackTab
  const initialFeedbackTab = locationFeedbackTab || FEEDBACK_BOARD_TAB.HDQT
  const [activeFeedbackTab, setActiveFeedbackTab] = useState(initialFeedbackTab)
  const {
    totalCount,
    feedbackData,
    commentData,
    page,
    size,
    setQueryParamsSignDocument,
  } = FeedbackProcedureStore
  const fetchFeedback = async () => {
    loadingAnimationStore.setTableLoading(true)
    try {
      setQueryParamsSignDocument({
        title: searchText,
        startDate: dateRange ? dateRange[0].format('DD-MM-YYYY') : null,
        endDate: dateRange ? dateRange[1].format('DD-MM-YYYY') : null,
        status: selectedStatus,
        boardType: getBoardTypeByTab(activeFeedbackTab),
        page: page,
        size: size,
      })

      await FeedbackProcedureStore.getFeedbackProcedureList()
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }

  const result = feedbackData.map(item => ({
    ...item,
    feedbackComments: item.feedbackComments.map(comment => ({
      ...comment,
      feedbackCommentDetails: Object.values(
        comment.feedbackCommentDetails
          .filter(detail => detail.voteType !== null)
          .reduce((acc, detail) => {
            const { feedbackUsername, createdAt } = detail
            if (
              !acc[feedbackUsername] ||
              new Date(acc[feedbackUsername].createdAt) < new Date(createdAt)
            ) {
              acc[feedbackUsername] = detail
            }
            return acc
          }, {})
      ),
    })),
  }))

  const { departmentWithUserListv2 } = selectPeopleStore

  const newFeedbackData = result?.map(item => {
    const isExpired = item.endDate
      ? moment(item.endDate).isBefore(moment())
      : false

    // Calculate dynamic total PIC count for this item
    const picAssignees =
      item.assignees?.filter(a => a.permission === 'PIC') || []
    const countedUsers = new Set()
    let totalPICCount = 0

    picAssignees.forEach(assignee => {
      if (assignee.assignee_type === 'USER') {
        if (!countedUsers.has(assignee.assignee_code)) {
          totalPICCount += 1
          countedUsers.add(assignee.assignee_code)
        }
      } else if (assignee.assignee_type === 'DPT') {
        const department = departmentWithUserListv2?.find(
          dept => dept.code === assignee.assignee_code
        )
        if (department?.users) {
          department.users.forEach(user => {
            if (!countedUsers.has(user.user_name)) {
              totalPICCount += 1
              countedUsers.add(user.user_name)
            }
          })
        }
      }
    })

    // Fallback to 1 to avoid division by zero, though PICs should always exist
    const denominator = totalPICCount || 1

    const itemOpinions = item.feedbackComments.map(comment => {
      const counts = { AGREE: 0, DISAGREE: 0, OTHER: 0 }
      comment.feedbackCommentDetails.forEach(detail => {
        if (detail.voteType in counts) {
          counts[detail.voteType] += 1
        }
      })

      if (item.isTTr) {
        counts.AGREE = Math.min(denominator, counts.AGREE + 1)
      }

      const totalVotes = counts.AGREE + counts.DISAGREE + counts.OTHER
      const agreePercentage = (counts.AGREE / denominator) * 100
      const disagreePercentage = (counts.DISAGREE / denominator) * 100
      const otherPercentage = (counts.OTHER / denominator) * 100

      let statusText = 'Đang xử lý'
      let statusType = 'warning'
      if (totalVotes >= denominator) {
        statusText = 'Hoàn thành'
        statusType = 'success'
      } else if (isExpired) {
        statusText = 'Quá hạn'
        statusType = 'danger'
      }

      return {
        ...comment,
        ...counts,
        statusText,
        statusType,
        agreePercentage: Number(agreePercentage.toFixed(2)),
        disagreePercentage: Number(disagreePercentage.toFixed(2)),
        otherPercentage: Number(otherPercentage.toFixed(2)),
      }
    })

    // Overall document status (summary)
    const minVotes =
      itemOpinions.length > 0
        ? Math.min(...itemOpinions.map(o => o.AGREE + o.DISAGREE + o.OTHER))
        : 0
    let overallStatusText = 'Đang xử lý'
    let overallStatusType = 'warning'
    if (minVotes >= denominator) {
      overallStatusText = 'Hoàn thành'
      overallStatusType = 'success'
    } else if (isExpired) {
      overallStatusText = 'Quá hạn'
      overallStatusType = 'danger'
    }

    return {
      ...item,
      itemOpinions,
      overallStatusText,
      overallStatusType,
    }
  })

  const handleOpenCreateFeedback = () => {
    setOpenFeedbackHDQT(true)
  }

  const handleCloseCreateFeedback = () => {
    setOpenFeedbackHDQT(false)
  }

  const handleStatusChange = value => {
    setSelectedStatus(value)
  }

  const handleChangeFeedbackTab = key => {
    runInAction(() => {
      FeedbackProcedureStore.page = 0
      FeedbackProcedureStore.queryParamsSignDocument.page = 0
    })
    setActiveFeedbackTab(key)
  }

  const locationPathname = location?.pathname || ''
  const locationSearch = location?.search || ''
  const locationHash = location?.hash || ''

  useEffect(() => {
    if (!locationPathname) return
    if (locationFeedbackTab === activeFeedbackTab) return

    history.replace({
      pathname: locationPathname,
      search: locationSearch,
      hash: locationHash,
      state: { feedbackTab: activeFeedbackTab },
    })
  }, [
    activeFeedbackTab,
    history,
    locationFeedbackTab,
    locationPathname,
    locationSearch,
    locationHash,
  ])

  const debouncedFetchFeedback = useMemo(() => debounce(fetchFeedback, 300), [
    searchText,
    dateRange,
    selectedStatus,
    activeFeedbackTab,
  ])

  useEffect(() => {
    debouncedFetchFeedback()
  }, [searchText, dateRange, selectedStatus, activeFeedbackTab])

  // Fetch department data on mount so % calculations are correct immediately
  useEffect(() => {
    if (selectPeopleStore.departmentWithUserListv2.length === 0) {
      selectPeopleStore
        .getDepartmentWithUsersV2()
        .catch(err => console.error('Failed to load department data:', err))
    }
  }, [])

  const handleSearchChange = e => {
    setSearchText(e.target.value)
  }

  const handleDateRangeChange = dates => {
    setDateRange(dates)
  }

  const exportFeedbackProcedure = async () => {
    try {
      loadingAnimationStore.showSpinner(true)
      // Use the already computed data if available, or fetch all and compute
      await ExportExcel(newFeedbackData)
    } catch (error) {
      console.log(error)
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const onChangePage = async (page, pageSize) => {
    runInAction(() => {
      FeedbackProcedureStore.page = page - 1
      FeedbackProcedureStore.size = pageSize
      FeedbackProcedureStore.queryParamsSignDocument.page = page - 1
      FeedbackProcedureStore.queryParamsSignDocument.size = pageSize
    })

    loadingAnimationStore.setTableLoading(true)

    try {
      await FeedbackProcedureStore.getFeedbackProcedureList()
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }

  const filteredFeedbackData = useMemo(
    () =>
      newFeedbackData.filter(
        record => getFeedbackTabFromRecord(record) === activeFeedbackTab
      ),
    [newFeedbackData, activeFeedbackTab]
  )

  const tableColumns = useMemo(
    () => [
      {
        title: 'Số PLYK',
        width: 120,
        render: (text, record) => {
          const recordTab = getFeedbackTabFromRecord(record)
          const suffix = getPlykSuffixByTab(recordTab)
          const displayNumber = toDisplayDocumentNumber(record.documentNumber)

          return (
            <span style={{ fontWeight: 500 }}>
              {displayNumber ? `${displayNumber}/${suffix}` : `—/${suffix}`}
            </span>
          )
        },
      },
      {
        title: 'Nội dung ý kiến',
        width: 400,
        render: (text, record, index) => {
          const getPlainText = html => {
            const div = document.createElement('div')
            div.innerHTML = html
            return div.textContent || div.innerText || ''
          }

          return (
            <div>
              {/* Header spacer - canh hàng với các cột khác */}
              <div
                style={{
                  marginBottom: '8px',
                  minHeight: '24px',
                  fontWeight: 600,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                dangerouslySetInnerHTML={{ __html: record.title }}
              />

              {/* Hiển thị các nội dung ý kiến */}
              {record.feedbackComments?.map((comment, idx) => {
                if (!comment.content) return null

                const plainText = getPlainText(comment.content)
                const truncatedText =
                  plainText.length > 100
                    ? plainText.substring(0, 80) + '...'
                    : plainText

                return (
                  <div
                    key={idx}
                    title={plainText}
                    style={{
                      borderTop: '1px solid #f0f0f0',
                      paddingTop: '4px',
                      paddingLeft: '12px',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '13px',
                    }}>
                    {'- ' + truncatedText}
                  </div>
                )
              })}
            </div>
          )
        },
      },
      {
        title: 'Ngày bắt đầu',
        width: 140,
        align: 'center',
        render: record => {
          const formattedDate = record?.startDate
            ? moment(record.startDate).format('HH:mm – DD/MM/YYYY')
            : ''
          return <p style={{ marginBottom: '0px' }}>{formattedDate}</p>
        },
      },
      {
        title: 'Ngày kết thúc',
        width: 140,
        align: 'center',
        render: record => {
          const formattedDate = record?.endDate
            ? moment(record.endDate).format('HH:mm – DD/MM/YYYY')
            : ''
          return <p style={{ marginBottom: '0px' }}>{formattedDate}</p>
        },
      },
      {
        title: 'Trạng thái',
        align: 'center',
        width: 120,
        render: record => {
          return (
            <div>
              <div style={{ marginBottom: '8px', minHeight: '24px' }}>
                &nbsp;
              </div>
              {record.itemOpinions?.map((opinion, idx) => {
                let color = 'orange'
                if (opinion.statusType === 'success') color = 'green'
                if (opinion.statusType === 'danger') color = 'red'
                return (
                  <div
                    key={idx}
                    style={{
                      borderTop: '1px solid #f0f0f0',
                      paddingTop: '4px',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <Tag
                      color={color}
                      style={{ borderRadius: 4, margin: 0, border: 'none' }}>
                      {opinion.statusText || 'Đang xử lý'}
                    </Tag>
                  </div>
                )
              })}
            </div>
          )
        },
      },
      {
        title: 'Tán thành',
        align: 'center',
        width: 100,
        render: record => (
          <div>
            <div style={{ marginBottom: '8px', minHeight: '24px' }}>&nbsp;</div>
            {record.itemOpinions?.map((opinion, idx) => (
              <div
                key={idx}
                style={{
                  borderTop: '1px solid #f0f0f0',
                  paddingTop: '4px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <p style={{ marginBottom: '0px' }}>
                  {opinion?.agreePercentage?.toFixed(2)}%
                </p>
              </div>
            ))}
          </div>
        ),
      },
      {
        title: 'Không tán thành',
        align: 'center',
        width: 100,
        render: record => (
          <div>
            <div style={{ marginBottom: '8px', minHeight: '24px' }}>&nbsp;</div>
            {record.itemOpinions?.map((opinion, idx) => (
              <div
                key={idx}
                style={{
                  borderTop: '1px solid #f0f0f0',
                  paddingTop: '4px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <p style={{ marginBottom: '0px' }}>
                  {opinion?.disagreePercentage?.toFixed(2)}%
                </p>
              </div>
            ))}
          </div>
        ),
      },
      ...(!isDelegateFeedbackTab(activeFeedbackTab)
        ? [
            {
              title: 'Không có ý kiến',
              align: 'center',
              width: 100,
              render: record => (
                <div>
                  <div style={{ marginBottom: '8px', minHeight: '24px' }}>
                    &nbsp;
                  </div>
                  {record.itemOpinions?.map((opinion, idx) => (
                    <div
                      key={idx}
                      style={{
                        borderTop: '1px solid #f0f0f0',
                        paddingTop: '4px',
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <p style={{ marginBottom: '0px' }}>
                        {opinion?.otherPercentage?.toFixed(2)}%
                      </p>
                    </div>
                  ))}
                </div>
              ),
            },
          ]
        : []),
    ],
    [activeFeedbackTab]
  )

  return (
    <>
      <DashboardLayout>
        <Helmet>
          <title>Phiếu lấy ý kiến | VIMC Quản lý văn bản VIMC</title>
        </Helmet>
        <AppPageHeader
          title="Lấy ý kiến HĐQT/ NĐD"
          addon={
            <>
              <Input
                style={{ maxWidth: 250 }}
                allowClear
                placeholder={'Tìm kiếm theo nội dung'}
                value={searchText}
                onChange={handleSearchChange}
              />
              <RangePicker
                format={'DD/MM/YYYY'}
                style={{ maxWidth: 250 }}
                value={dateRange}
                onChange={handleDateRangeChange}
              />

              <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__HDQT}>
                <Button
                  // onClick={() => history.push(`/risk/create?type=${type}`)}
                  onClick={handleOpenCreateFeedback}
                  type={'primary'}
                  className="icon-btn"
                  icon={
                    <AddCircleOutlineIcon
                      style={{ color: '#fff', width: 16, height: 16 }}
                    />
                  }>
                  Tạo mới
                </Button>
              </AccessControlAction>
            </>
          }
        />

        <ContentBlockWrapper>
          <Tabs
            activeKey={activeFeedbackTab}
            onChange={handleChangeFeedbackTab}
            style={{ marginBottom: 16 }}>
            <TabPane
              tab="Ý kiến biểu quyết HĐQT"
              key={FEEDBACK_BOARD_TAB.HDQT}
            />
            <TabPane
              tab="Ý kiến biểu quyết Người đại diện"
              key={FEEDBACK_BOARD_TAB.DELEGATE}
            />
          </Tabs>

          <AppTable
            rowKey={record => record.code}
            onRow={record => {
              return {
                onClick: () => {
                  const recordTab = getFeedbackTabFromRecord(record)
                  history.push(
                    `/feedback-hdqt/${recordTab}/view/${record.code}`
                  )
                },
              }
            }}
            dataSource={filteredFeedbackData}
            columns={tableColumns}
            pagination={false}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={'Không có dữ liệu'}
                />
              ),
            }}
            loading={{
              tip: 'Đang tải phiếu ý kiến...',
              spinning: loadingAnimationStore.tableLoading,
            }}
          />

          <AppTablePagination
            current={page + 1}
            total={totalCount}
            pageSize={size}
            onChange={onChangePage}
          />
        </ContentBlockWrapper>
      </DashboardLayout>
      {openFeedbackHDQT && (
        <FeedbackBoardCreate
          isVisible={openFeedbackHDQT}
          setIsVisible={setOpenFeedbackHDQT}
          handleCancel={handleCloseCreateFeedback}
          fetchFeedback={fetchFeedback}
          feedbackTab={activeFeedbackTab}
        />
      )}
    </>
  )
}

export default inject(
  'selectUserStore',
  'documentAccessibilityStore',
  'selectPeopleStore',
  'loadingAnimationStore',
  'authenticationStore',
  'FeedbackProcedureStore'
)(observer(FeedbackBoardPage))
